import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputLabel,
    FormControl,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ReCAPTCHA from 'react-google-recaptcha';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import ApiHelper from 'utils/apiHelper';
import { useSelector } from 'react-redux';
import { validateEmail, validateUsername } from 'utils/commonHelpers';
import useRecaptchaSiteKey from 'hooks/useRecaptchaSiteKey';

const AddUserDialog = ({ onClose, userGroupsData, limitFields }) => {
    const recaptchaSiteKey = useRecaptchaSiteKey();
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [userGroupId, setUserGroupId] = useState([]);
    const [loading, setLoading] = useState(false);
    const { userInfo } = useSelector((state) => state.auth);

    const handleChangeUserGroupId = (event) => {
        const {
            target: { value }
        } = event;
        setUserGroupId(typeof value === 'string' ? value.split(',') : value);
    };

    const handleChangeUserName = (e) => {
        if (e.target.value.length > limitFields['username']) {
            toast.warning('This field must be ' + limitFields['username'] + ' characters or less.');
        } else {
            setUsername(e.target.value);
        }
    };

    const handleChangeEmail = (e) => {
        if (e.target.value.length > limitFields['email']) {
            toast.warning('This field must be ' + limitFields['email'] + ' characters or less.');
        } else {
            setEmail(e.target.value);
        }
    };

    const handleSubmit = () => {
        if (username === '') {
            toast.info('Username is required.');
            return true;
        } else {
            if (!validateUsername(username)) {
                toast.info('The username may contain letters, numbers, and _ . - characters');
                return true;
            }
            if (email === '') {
                toast.info('Email is required.');
                return true;
            }
            if (!validateEmail(email)) {
                toast.info('Invalid email format.');
                return true;
            }
            if (email !== confirmEmail) {
                toast.info(`Confirm email is incorrect`);
                return true;
            }
        }
        if (userGroupId.length === 0) {
            toast.info('Please select the user group');
            return true;
        }
        setLoading(true);

        ApiHelper.post('/api/management/users/', {
            username: username,
            email: email,
            user_group_names: userGroupId
        })
            .then(() => {
                toast.success('Successfully added.');
                onClose();
                setLoading(false);
            })
            .catch((err) => {
                if (err.response.data.message !== '') {
                    toast.error(`Failed! ${err.response.data.message}`);
                } else {
                    toast.error('Failed to add a new user');
                }
                setLoading(false);
            });
    };

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle sx={{ fontSize: '16px' }}>Add New User</DialogTitle>
            <DialogContent sx={{ minWidth: '500px' }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Username"
                        value={username}
                        onChange={(e) => handleChangeUserName(e)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Email"
                        value={email}
                        onChange={(e) => handleChangeEmail(e)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Confirm Email"
                        value={confirmEmail}
                        onChange={(e) => setConfirmEmail(e.target.value)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <FormControl sx={{ width: '100%' }}>
                        <InputLabel>Select the user group</InputLabel>
                        <Select
                            displayEmpty
                            value={userGroupId}
                            name="userGroupId"
                            multiple
                            input={<OutlinedInput label="Select the user group" />}
                            onChange={handleChangeUserGroupId}
                            renderValue={(selected) => selected.join(', ')}
                        >
                            {userGroupsData.map((data, key) => (
                                <MenuItem value={data.name} key={key}>
                                    <Checkbox checked={userGroupId.indexOf(data.name) > -1} />
                                    {data.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ mt: 3, mb: 3 }}>
                    <ReCAPTCHA sitekey={recaptchaSiteKey} onChange={() => setIsCaptchaSuccess(true)} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <LoadingButton onClick={handleSubmit} loading={loading} color="primary" variant="contained">
                    <span>Submit</span>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

AddUserDialog.propTypes = {
    onClose: PropTypes.func,
    userGroupsData: PropTypes.array,
    limitFields: PropTypes.object
};

export default AddUserDialog;
